import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, Observable, Subject, catchError, combineLatest, distinctUntilChanged, finalize, fromEvent, of, switchMap, take, takeUntil } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

import {
  selectCalLotsSlice,
  selectCalculationAdjustedResult,
  selectIsCalAdjustFinal,
  selectCalculationResult,
  selectSampleDataLoading,
  selectSampleDataStatus,
  selectAssaySummaryColumnSelection,
  selectPanelName,
  selectPanelSpecsSlice,
  selectPatientFileMetadata,
  selectRefKitLot,
  selectReportContentItemsSlice,
  selectRunSamplesSlice,
  selectRunType,
  selectTestSpec,
  selectQcLotsSlice,
  selectAppState,
  selectIsStudySaved
} from '../../state/selectors';
import { setAssaySummaryColumnSelection, setAuditTrail } from '../../state/actions';
import { AuditTrailEntry } from '../../../shared/models/study.model';
import { FileMetaData, ReportContentItem, ReportContentItemResponse, Sample } from '../../../shared/models/run.model';
import { AssayData, AssayDataCalCurve, CalculationResult, Controls, QCControl, RegressionStats, RunStatus, Vial } from '../../../shared/models/result.model';

type ControlTestVial = Vial & {
  alertStatus?:  string | null;
};
import {
  ColumnSelection,
  CommonDialogData,
  PrintColumnSelectionDialogData,
  PrintColumnSelectionResult
} from '../../../shared/models/dialog.model';
import { PrintColumnSelectionDialogComponent } from '../../../shared/components/print-column-selection-dialog/print-column-selection-dialog.component';
import {
  applyPrintColumnSelection,
  chunkColumnsForPrint,
  describePrintPage,
  PrintPageFit
} from '../../../shared/utils/print-column.util';
import { isPreBulkRunType, isRmqcRunType, isStabilityRunType } from '../../../shared/utils/run-type.util';
import { AppUtilityService } from '../../../shared/services/app-utility.service';
import { Assay_Specs, BulkMaterials, Panel } from '../../../shared/models/assays.model';
import { deriveRunValidityStatus, getStatusClassFromResult, normalizeStatusValue, resultPassFailAlert, runValidityPassFailAlertCount } from '../../../shared/utils/assay-helper ';
import { transformToCamelCase, displayOrDash, formatSpecRange, formatComputedNumber, saveBlobAsFile, resolveCalLotForRegion, adjustedColumnLabel } from '../../../shared/utils/helper';
import { apiPaths } from '../../core/constants/api.const';
import { QcLot } from '../../../shared/models/qc-lot.model';
import { CalLot } from '../../../shared/models/cal-lot.model';
import { AssayWorkbookExportService } from '../services/assay-workbook-export.service';
import { ReportDownloadStatusService } from '../../../shared/services/report-download-status.service';
import { ensureSampleData } from '../../state/actions';

/** Number of collapsible sections; the fixed five plus one per range. */
const SECTION_COUNT = 16;
/** Range N is section RANGE_SECTION_OFFSET + N. */
const RANGE_SECTION_OFFSET = 5;
/** Report content fields rendered in the Summary Status section. */
const SUMMARY_STATUS_FIELDS: (keyof ReportContentItem)[] = [
  'beadCouplingDate', 'coupledBeadLn', 'dyedBeadLn', 'progenitorLn', 'antigenLn',
  'antigenLoadLn', 'coupledBeadPn', 'ligandPn', 'beadReagentPn', 'description'
];
/** The Cal Curve Parameters rows are seven metrics, each reading the same six fields. */
const CAL_CURVE_PARAMETER_METRICS = [
  'zeroConcentrationRfi', 'cutOffRfi', 'estimatedHighAssayRangeRfi', 'percentModulation', 'calBeadToBeadCV',
  'lowEndSeparation', 'highEndSeparation'
];
const CAL_CURVE_PARAMETER_SUFFIXES = ['', 'Result', 'SpecMin', 'SpecMax', 'SpecAlertMin', 'SpecAlertMax'];
/**
 * @description The AssaySummaryComponent is responsible for displaying and analyzing assay summary data,
 * including calibration, validity, and result statuses for each sample.
 * It supports print views, tabular column filtering, and visual section toggles.
 */
@Component({
  selector: 'app-assay-summary',
  templateUrl: './assay-summary.component.html',
  styleUrl: './assay-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssaySummaryComponent implements OnInit, OnDestroy, AfterViewInit {
  isStudySaved$ = this.store.select(selectIsStudySaved);
  @Input() tableId = 'assay-table';
  @Input() exportMode = false;
  @ViewChild('assaySummaryScroll', { static: false }) assaySummaryScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild('assaySummaryTable', { static: false }) assaySummaryTableRef?: ElementRef<HTMLTableElement>;

  panelName$ = this.store.select(selectPanelName); /** Observable stream for selected panel name from the store */
  private readonly patientFileMetadata$: Observable<FileMetaData[]> = this.store.select(selectPatientFileMetadata);
  private readonly reportContentItems$: Observable<ReportContentItemResponse | null> = this.store.select(selectReportContentItemsSlice);
  private readonly runSamples$: Observable<Sample[]> = this.store.select(selectRunSamplesSlice);
  private readonly panelSpecs$: Observable<Panel | null> = this.store.select(selectPanelSpecsSlice);
  private readonly calculationResult$: Observable<CalculationResult | null> = this.store.select(selectCalculationResult);
  private readonly calculationAdjustedResult$: Observable<CalculationResult | null> = this.store.select(selectCalculationAdjustedResult);
  private readonly calLots$: Observable<CalLot[]> = this.store.select(selectCalLotsSlice);
  private readonly qcLots$: Observable<QcLot[]> = this.store.select(selectQcLotsSlice);
  private readonly runType$: Observable<string> = this.store.select(selectRunType);
  private readonly testSpec$: Observable<string> = this.store.select(selectTestSpec);
  private readonly refKitLot$: Observable<string> = this.store.select(selectRefKitLot);
  private readonly assaySummaryColumnSelection$ = this.store.select(selectAssaySummaryColumnSelection);
  sampleDataLoading = false;
  sectionVisibility = new Array<boolean>();   /** Array tracking visibility of each section in the UI */
  /** Per-section flag; false when every row in that section would render empty. */
  sectionHasData = new Array<boolean>(SECTION_COUNT).fill(false);
  patientFileMetadata: FileMetaData[] = [];   /** Metadata for patient files */
  reportContent: ReportContentItem[] = [];  /** Full report content for the assay */
  runSamples: Sample[] = [];  /** All samples for the assay run */
  filteredReportContent: ReportContentItem[] = [];  /** Subset of report content used for display/filtering */
  tableColumns!: ColumnSelection[];  /** Table columns dynamically generated from lots */
  selectedColumns!: ColumnSelection[];   /** Currently selected table columns for display */
  isPrintView = false;  /** Whether print view is active */
  isPrintMode = false;  /** Whether print mode (for layout) is enabled */
  printTruncatedReport = true; /** Print Truncated Report checkbox state */

  /** Print View, Print Truncated Report and Export As Workbook are offered for Stability runs only. */
  get showStabilityOutputs(): boolean {
    return isStabilityRunType(this.runType);
  }
  operatorName = '';
  studyName = '';
  /** True once the study is saved (not a draft). The backend report reads the
   *  saved study, so an unsaved study cannot be reported on. */
  isStudySaved = false;
  /** True while the study carries edits that have not reached the server. Saved-once is
   *  not enough for a controlled document: the report would state the last saved study. */
  areStudyChangesPending = false;
  specVersion = '';
  appVersion = '';
  isMenuOpen = false;   /** Whether the dropdown menu is currently open */
  ranges = [1, 2, 3, 4];  /** Default range numbers */
  protected destroy$ = new Subject<boolean>(); /** RxJS subject to clean up subscriptions */
  /** Whether the adjusted columns hold a Calibrator Adjustment's Final run. */
  isFinal = false;

  get adjustedLabel(): string {
    return adjustedColumnLabel(this.isFinal);
  }
  bulkMaterial: BulkMaterials[] = [];  /** Bulk materials associated with the assay panel */
  calResponseFilteredData: AssayData[] = [];  /** Original calculation results */
  calResponseFilteredAdjustedData: AssayData[] = [];  /** Adjusted calculation results */
  calResponseCombinedData: AssayData[] = []; /** Combined original and adjusted results */
  calResponseCombinedBackupData: AssayData[] = []; /** Backup of the combined data for restoring filtered results */
  regressionRangeStatus: { rangeNo: number; status: string }[] = [];  /** Status for each regression range */
  calCurveParameterStatus: string = 'NA';  /** Overall calibration curve parameter status */
  calculationResult!: CalculationResult | null | undefined;  /** Original calculation result object */
  calculationAdjustedResult!: CalculationResult | null | undefined; /** Adjusted calculation result object */
  runValidityStatus: string = 'NA'; /** Overall run validity status */
  calibrationCurveSectionStatus: string = 'NA'; /** Aggregated status for Calibration Curve section */
  controlQcSectionStatus: string = 'NA'; /** Aggregated status for Control QC section */
  controlTestSectionStatus: string = 'NA'; /** Aggregated status for Control Test section */
  panelSpecs!: Panel | null;   /** Panel specifications associated with the assay */
  /** Utility functions bound for template use */
  getStatusClassFromResult = getStatusClassFromResult;
  runValidityPassFailAlertCount = runValidityPassFailAlertCount;
  resultPassFailAlert = resultPassFailAlert;
  qcLots!: QcLot[]; /** control lots */
  calLots!: CalLot[]; /** Calibration lots */
  runType = ''; /** Cached run type for template binding */
  testSpec = ''; /** Cached test specification for template binding */
  refKitLot = ''; /** Reference kit lot number for column highlighting */
  /**
   * The row labels here are the statistic names down the side; the data columns hold one number
   * each. Chunking is derived from these, not from a column count.
   */
  readonly printPageFit: PrintPageFit = { labelWidthMm: 58, columnWidthMm: 22 };
  private printPagesSnapshot: AssayData[][] = [];
  /** Null until the operator narrows the print selection; see `printColumnsForRebuild`. */
  private printColumnSelection: ColumnSelection[] | null = null;
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  controlQcLevels: number[] = []; /** Level numbers surfaced in the Control QC table */
  controlTestLevels: number[] = []; /** Level numbers surfaced in the Control Test table */
  controlTestLots: string[] = []; /** Ordered list of control test lots displayed per section */
  private originalControlQcMap = new Map<string, QCControl>(); /** QC data for original calculations */
  private adjustedControlQcMap = new Map<string, QCControl>(); /** QC data for adjusted calculations */
  private originalControlTestMap = new Map<string, QCControl[]>(); /** Control test data for original calculations */
  private adjustedControlTestMap = new Map<string, QCControl[]>(); /** Control test data for adjusted calculations */
  private lastSampleDataFresh: boolean | null = null;
  private headerResizeObserver?: ResizeObserver;
  private resizeFallbackListener: (() => void) | null = null;
  private readonly controlQcMapCache = new WeakMap<Controls[], Map<string, QCControl>>();
  private readonly controlTestMapCache = new WeakMap<Controls[], Map<string, QCControl[]>>();
  private readonly assaySpecsByRegionCache = new WeakMap<Assay_Specs[], Map<number, Assay_Specs[]>>();
  /** Cal lot number per region, resolved when a calculation response arrives. */
  private readonly calibratorLotSnapshot = new Map<number, string>();
  private readonly regressionStatsMapCache =new WeakMap<CalculationResult, Map<string, RegressionStats>>();
  private readonly runStatusMapCache = new WeakMap<CalculationResult, Map<string, RunStatus>>();
  private hasAppliedInitialHorizontalNudge = false;
  private lastBuildInputs?: {
    reportContent: ReportContentItem[];
    runSamples: Sample[];
    panelSpecs: Panel | null;
    calculationResult: CalculationResult | null;
    calculationAdjustedResult: CalculationResult | null;
    calLots: CalLot[];
    qcLots: QcLot[];
    runType: string;
    testSpec: string;
    refKitLot: string;
    columnSelectionKey: string;
    isPrintView: boolean;
  };
  private lastEnrichedInputs?: {
    baseReportContent: ReportContentItem[];
    calculationResult: CalculationResult | null;
    calculationAdjustedResult: CalculationResult | null;
    refKitLot: string;
    enrichedReportContent: ReportContentItem[];
  };
  /** Dedicated trackBy to keep the bulk material rows stable when the array rehydrates. */
  trackBulkMaterial = (_index: number, item: BulkMaterials) => item?.bulk_material_description ?? _index;
  trackByValue = (_index: number, value: string | number | null | undefined) => value ?? _index;
  /**
   * TrackBy helper for the many calResponseCombinedData loops. Using the reportContentItemId and
   * value assignment keeps both the original and adjusted rows stable even when NgRx emits a fresh array.
   */
  trackAssayRow = (_index: number, assay: AssayData) =>
    `${assay?.reportContentItems?.reportContentItemId ?? 'unknown'}-${assay?.valueAssigned ?? 'Original'}`;

  get printPages(): AssayData[][] {
    return this.printPagesSnapshot;
  }

  private buildPrintPages(): AssayData[][] {
    return chunkColumnsForPrint(this.calResponseCombinedData, this.printPageFit);
  }

  /** "Columns 7-12 of 22 · page 2 of 4" over a chunk page; empty when the report was not split. */
  printPageCaption(pageIndex: number): string {
    return describePrintPage(pageIndex, this.printPages);
  }

  shouldRenderReportContentRow(field: keyof ReportContentItem): boolean {
    if (!this.calResponseCombinedData?.length) {
      return false;
    }
    return this.calResponseCombinedData.some(item =>
      displayOrDash((item.reportContentItems as Record<string, unknown>)[field]) !== '-'
    );
  }

  shouldRenderBulkMaterialRow(description: string): boolean {
    if (!this.calResponseCombinedData?.length) {
      return false;
    }
    const key = this.transformedVariableColumns(description);
    return this.calResponseCombinedData.some(item =>
      displayOrDash((item.reportContentItems as Record<string, unknown>)[key]) !== '-'
    );
  }

  /**
 * Constructor for AssaySummaryComponent.
 * @param router Angular Router for navigation
 * @param store NgRx Store for state management
 * @param appUtilityService Shared application utility service
 * @param cdRef Change detector reference
 */
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store,
    private appUtilityService: AppUtilityService,
    private assayWorkbookExportService: AssayWorkbookExportService,
    private reportDownloadStatus: ReportDownloadStatusService,
    private dialog: MatDialog,
    public cdRef: ChangeDetectorRef,
  ) { }

  /**
 * Angular lifecycle hook: Initializes the component and subscribes to study data.
 */
  ngOnInit() {
    this.store.select(selectIsCalAdjustFinal).pipe(takeUntil(this.destroy$)).subscribe(isFinal => {
      this.isFinal = isFinal;
      this.cdRef.markForCheck();
    });
    this.isPrintView = this.exportMode ? false : this.router?.url?.includes('print');
    if (this.isPrintView) {
      const truncatedParam = this.route.snapshot.queryParamMap.get('truncated');
      if (truncatedParam !== null) {
        this.printTruncatedReport = truncatedParam === '1' || truncatedParam.toLowerCase() === 'true';
      }
    }
    this.monitorSampleDataStatus();

    // Subscribe only to the slices that this template actually needs so OnPush stays efficient.
    combineLatest([
      this.patientFileMetadata$,
      this.reportContentItems$,
      this.runSamples$,
      this.panelSpecs$,
      this.calculationResult$,
      this.calculationAdjustedResult$,
      this.calLots$,
      this.qcLots$,
      this.runType$,
      this.testSpec$,
      this.refKitLot$,
      this.assaySummaryColumnSelection$,
      this.store.select(selectAppState)
    ])
      .pipe(
        distinctUntilChanged((prev, curr) => prev.length === curr.length && prev.every((value, index) => value === curr[index])),
        takeUntil(this.destroy$)
      )
      .subscribe(([
        patientMetadata,
        reportContentResponse,
        runSamples,
        panelSpecs,
        calculationResult,
        calculationAdjustedResult,
        calLots,
        qcLots,
        runType,
        testSpec,
        refKitLot,
        assaySummaryColumnSelection,
        appState
      ]) => {
        // Persist each slice locally so the template can stay simple while the component remains OnPush.
        this.patientFileMetadata = patientMetadata;
        // Set before the sort below: the comparator reads refKitLot to pin the reference lot first.
        this.refKitLot = refKitLot;
        const baseReportContent = reportContentResponse?.reportContentItems ?? [];
        const enrichedReportContent = this.getEnrichedReportContent(
          baseReportContent,
          calculationResult,
          calculationAdjustedResult
        );
        this.reportContent = enrichedReportContent;
        this.filteredReportContent = enrichedReportContent;
        const isNewCalculation =
          calculationResult !== this.calculationResult ||
          calculationAdjustedResult !== this.calculationAdjustedResult;
        this.runSamples = runSamples ?? [];
        this.panelSpecs = panelSpecs;
        this.bulkMaterial = panelSpecs?.bulkMaterials ?? [];
        this.calculationResult = calculationResult;
        this.calculationAdjustedResult = calculationAdjustedResult;
        this.calLots = calLots ?? [];
        if (isNewCalculation) {
          this.snapshotCalibratorLots();
        }
        this.qcLots = qcLots ?? [];
        this.runType = runType;
        this.testSpec = testSpec;
        this.operatorName = appState?.loggedInUser ?? '';
        this.studyName = appState?.selectedStudy?.studyName ?? '';
        this.isStudySaved = !!appState?.selectedStudy?.studyId && !appState?.selectedStudy?.isDraft;
        this.areStudyChangesPending = !!appState?.areStudyChangesPending;
        this.specVersion = appState?.selectedStudy?.panelSpecs?.revisionNumber ?? '';
        this.appVersion = appState?.appVersion ?? '';
        const nextSelectionKey = this.buildColumnSelectionKey(assaySummaryColumnSelection);
        if (this.shouldRebuildAssaySummary(nextSelectionKey)) {
          this.tableColumns = this.buildTableColumns();
          this.sectionVisibility = new Array(SECTION_COUNT).fill(true);
          this.buildAssayDataSets();
          this.applyStoredColumnSelection(assaySummaryColumnSelection);
          this.refreshSectionDataFlags();

          if (this.isPrintView) {
            // The print route is print mode; there is no other reason to be on it. This used to
            // be switched on by the print menu opening over the page, which left the layout in
            // screen mode if the operator dismissed the menu.
            this.isPrintMode = true;
            this.toggleAllSections(true);
            // Carries the operator's choice across the rebuild. `applyStoredColumnSelection`
            // above works from the screen's stored selection, which is empty on this route, so
            // it has just re-selected every column — which is what sent the whole table to the
            // printer however few columns had been chosen in the preview. `tableColumns` is
            // corrected too, not just the data derived from it: it is what the preview bar binds
            // its checkboxes to, and it feeds the next rebuild.
            this.tableColumns = this.printColumnsForRebuild();
            this.handleSelectColumns(this.tableColumns, false);
            this.printPagesSnapshot = this.buildPrintPages();
          }
        }

        this.cdRef.markForCheck();
        this.scheduleEmptyRowPrune();
        this.scheduleInitialHorizontalNudge();
      });

    this.store.select(selectSampleDataLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(sampleDataLoading => {
        this.sampleDataLoading = sampleDataLoading;
        this.cdRef.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    this.setupHeaderHeightObserver();
    this.setupSectionHeaderScrollSync();
    this.scheduleEmptyRowPrune();
    this.scheduleInitialHorizontalNudge();
  }

  private setupSectionHeaderScrollSync(): void {
    const scrollEl = this.assaySummaryScrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }

    const applySync = () => {
      this.updateScrolledPastControlSectionRows();
    };

    applySync();
    fromEvent(scrollEl, 'scroll')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => applySync());
  }

  private updateScrolledPastControlSectionRows(): void {
    const scrollEl = this.assaySummaryScrollRef?.nativeElement;
    const tableEl = this.assaySummaryTableRef?.nativeElement;
    if (!scrollEl || !tableEl) {
      return;
    }

    const tableTop = scrollEl.getBoundingClientRect().top;
    const headerHeight = tableEl.querySelector('thead')?.getBoundingClientRect().height ?? 0;
    const cutoff = tableTop + headerHeight;

    const controlRows = Array.from(
      tableEl.querySelectorAll('tbody tr.control-section-row')
    ) as HTMLTableRowElement[];

    controlRows.forEach(row => {
      const rowRect = row.getBoundingClientRect();
      const isScrolledPast = rowRect.bottom <= cutoff;
      row.classList.toggle('control-section-row-scrolled-past', isScrolledPast);
    });
  }

  /**
   * Trigger pull_sample_data when cached sample rows are stale so dependent views stay current.
   */
  private monitorSampleDataStatus(): void {
    this.store.select(selectSampleDataStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        const becameStale = !status.sampleDataFresh && (this.lastSampleDataFresh === null || this.lastSampleDataFresh);
        if (becameStale && !status.sampleDataLoading) {
          this.store.dispatch(ensureSampleData());
        }
        this.lastSampleDataFresh = status.sampleDataFresh;
      });
  }

  private scheduleEmptyRowPrune(): void {
    if (!this.assaySummaryTableRef?.nativeElement) {
      return;
    }
    window.setTimeout(() => {
      if (!this.assaySummaryTableRef?.nativeElement) {
        return;
      }
      this.pruneEmptyRows();
    }, 0);
  }

  private scheduleInitialHorizontalNudge(): void {
    if (this.hasAppliedInitialHorizontalNudge) {
      return;
    }

    window.setTimeout(() => {
      const scrollEl = this.assaySummaryScrollRef?.nativeElement;
      if (!scrollEl) {
        return;
      }

      // Tiny initial nudge avoids first-column border paint glitches until manual scroll.
      if (scrollEl.scrollLeft === 0) {
        scrollEl.scrollLeft = 2;
        scrollEl.dispatchEvent(new Event('scroll'));
        window.setTimeout(() => {
          scrollEl.scrollLeft = 0;
          scrollEl.dispatchEvent(new Event('scroll'));
        }, 0);
      }

      this.hasAppliedInitialHorizontalNudge = true;
    }, 0);
  }

  private pruneEmptyRows(): void {
    const tableEl = this.assaySummaryTableRef?.nativeElement;
    if (!tableEl) {
      return;
    }
    const rows = Array.from(tableEl.querySelectorAll('tr')) as HTMLTableRowElement[];
    rows.forEach(row => {
      if (
        row.classList.contains('subsection-header') ||
        row.classList.contains('subsection-header-print2') ||
        row.classList.contains('subsection-header-print') ||
        row.classList.contains('control-section-row')
      ) {
        row.style.removeProperty('display');
        return;
      }
      const cells = Array.from(row.querySelectorAll('td'));
      if (cells.length <= 1) {
        row.style.removeProperty('display');
        return;
      }
      const dataCells = cells.slice(1);
      const hasValue = dataCells.some(cell => {
        const text = (cell.textContent ?? '').trim();
        return text !== '' && text !== '-';
      });
      row.style.display = hasValue ? '' : 'none';
    });
    this.applyPreviewPrintGaps(tableEl);
  }

  private applyPreviewPrintGaps(tableEl: HTMLTableElement): void {
    const tbody = tableEl.tBodies?.[0];
    if (!tbody) {
      return;
    }

    const existingGapRows = Array.from(tbody.querySelectorAll('tr.preview-print-gap-row'));
    existingGapRows.forEach(gapRow => gapRow.remove());

    if (!this.isPrintView || !this.isPrintMode) {
      return;
    }

    const visibleRows = Array.from(tbody.querySelectorAll('tr'))
      .filter((row): row is HTMLTableRowElement => row instanceof HTMLTableRowElement)
      .filter(row => !row.classList.contains('preview-print-gap-row'))
      .filter(row => row.style.display !== 'none');

    if (!visibleRows.length) {
      return;
    }

    const baseRowHeight = visibleRows[0].getBoundingClientRect().height || 24;
    const gapHeight = Math.round(baseRowHeight * 4);
    let nextBreakIndex = 40;

    while (nextBreakIndex < visibleRows.length) {
      const anchorRow = visibleRows[nextBreakIndex - 1];
      if (!anchorRow?.parentElement) {
        nextBreakIndex += 52;
        continue;
      }

      const gapRow = document.createElement('tr');
      gapRow.className = 'preview-print-gap-row';
      const gapCell = document.createElement('td');
      gapCell.colSpan = Math.max(anchorRow.cells.length, 1);
      gapCell.style.height = `${gapHeight}px`;
      gapCell.style.padding = '0';
      gapCell.style.border = '0';
      gapCell.style.background = '#fff';
      gapRow.appendChild(gapCell);
      anchorRow.insertAdjacentElement('afterend', gapRow);
      nextBreakIndex += 52;
    }
  }

  private shouldRebuildAssaySummary(nextColumnSelectionKey: string): boolean {
    if (!this.lastBuildInputs) {
      this.lastBuildInputs = {
        reportContent: this.reportContent,
        runSamples: this.runSamples,
        panelSpecs: this.panelSpecs,
        calculationResult: this.calculationResult ?? null,
        calculationAdjustedResult: this.calculationAdjustedResult ?? null,
        calLots: this.calLots ?? [],
        qcLots: this.qcLots ?? [],
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
      return true;
    }

    const last = this.lastBuildInputs;
    const shouldRebuild = (
      last.reportContent !== this.reportContent ||
      last.runSamples !== this.runSamples ||
      last.panelSpecs !== this.panelSpecs ||
      last.calculationResult !== (this.calculationResult ?? null) ||
      last.calculationAdjustedResult !== (this.calculationAdjustedResult ?? null) ||
      last.calLots !== (this.calLots ?? []) ||
      last.qcLots !== (this.qcLots ?? []) ||
      last.runType !== this.runType ||
      last.testSpec !== this.testSpec ||
      last.refKitLot !== this.refKitLot ||
      last.columnSelectionKey !== nextColumnSelectionKey ||
      last.isPrintView !== this.isPrintView
    );

    if (shouldRebuild) {
      this.lastBuildInputs = {
        reportContent: this.reportContent,
        runSamples: this.runSamples,
        panelSpecs: this.panelSpecs,
        calculationResult: this.calculationResult ?? null,
        calculationAdjustedResult: this.calculationAdjustedResult ?? null,
        calLots: this.calLots ?? [],
        qcLots: this.qcLots ?? [],
        runType: this.runType,
        testSpec: this.testSpec,
        refKitLot: this.refKitLot,
        columnSelectionKey: nextColumnSelectionKey,
        isPrintView: this.isPrintView
      };
    }

    return shouldRebuild;
  }

  private buildColumnSelectionKey(selections: string[] | null | undefined): string {
    if (!selections?.length) {
      return '';
    }
    return selections
      .slice()
      .sort()
      .join('|');
  }

  private getEnrichedReportContent(
    baseReportContent: ReportContentItem[],
    calculationResult: CalculationResult | null,
    calculationAdjustedResult: CalculationResult | null
  ): ReportContentItem[] {
    // refKitLot is part of the key because the sort pins the reference lot first.
    const refKitLot = this.refKitLot ?? '';
    if (
      this.lastEnrichedInputs &&
      this.lastEnrichedInputs.baseReportContent === baseReportContent &&
      this.lastEnrichedInputs.calculationResult === calculationResult &&
      this.lastEnrichedInputs.calculationAdjustedResult === calculationAdjustedResult &&
      this.lastEnrichedInputs.refKitLot === refKitLot
    ) {
      return this.lastEnrichedInputs.enrichedReportContent;
    }

    const sortedReportContent = this.sortReportContent(baseReportContent);
    const enrichedReportContent = this.appendMeanColumnsToReportContent(
      sortedReportContent,
      calculationResult,
      calculationAdjustedResult
    );
    this.lastEnrichedInputs = {
      baseReportContent,
      calculationResult,
      calculationAdjustedResult,
      refKitLot,
      enrichedReportContent
    };
    return enrichedReportContent;
  }

  private sortReportContent(reportContent: ReportContentItem[]): ReportContentItem[] {
    return [...(reportContent ?? [])].sort((a, b) => this.compareReportContentItems(a, b));
  }

  /**
  * Returns the specified range variable from assay data.
  * @param materialItem Assay data object
  * @param index Range index (1-based)
  * @param variableName Name of the variable to retrieve
  * @returns The value of the variable or null
  */
  getRangeData(materialItem: AssayData, index: number, variableName: string) {
    const regressionStats = materialItem.regressionStats;
    const range = regressionStats?.ranges[index - 1];

    if (range && variableName in range) {
      return (range as unknown as Record<string, unknown>)[variableName];
    } else {
      return null;
    }
  }

  /**
   * Produces a "<low> to <high>" string for spec rows or "-" when either side is missing.
   * Centralizing this logic keeps every slope/intercept/mean block consistent and avoids
   * scattering quote formatting across the template.
   */
  formatRangeSpan(materialItem: AssayData, index: number, lowKey: string, highKey: string): string {
    const low = this.getRangeData(materialItem, index, lowKey);
    const high = this.getRangeData(materialItem, index, highKey);
    return this.buildSpanString(low, high);
  }

  /**
   * Calculates derived numeric rows (e.g., 1.96 * SD) and returns a printable string or "-".
   */
  getScaledRangeValue(materialItem: AssayData, index: number, variableName: string, multiplier: number): string {
    const value = this.coerceNumber(this.getRangeData(materialItem, index, variableName));
    if (value === null) {
      return '-';
    }
    const scaled = value * multiplier;
    if (!Number.isFinite(scaled)) {
      return '-';
    }
    return this.formatNumberWithPrecision(scaled, this.getResultPrecision(materialItem));
  }

  /**
   * Formats a numeric range value using the column's declared Result Decimal Precision.
   * Falls back to the raw value (or '-') if the payload is non-numeric.
   */
  formatRangeNumber(materialItem: AssayData, index: number, variableName: string): string {
    const precision = this.getResultPrecision(materialItem);
    const value = this.getRangeData(materialItem, index, variableName);
    const numeric = this.coerceNumber(value);
    if (numeric === null) {
      return this.displayOrDash(value);
    }
    return this.formatNumberWithPrecision(numeric, precision);
  }

  /**
   * Returns the R^2 value for a range, tolerating either rsquared or the legacy rsqaured key.
   */
  getRsquared(materialItem: AssayData, index: number): string {
    const range = materialItem.regressionStats?.ranges[index - 1];
    const rangeRecord = range as unknown as Record<string, unknown> | undefined;
    const rsValue = rangeRecord?.['rsquared'] ?? rangeRecord?.['rsqaured'] ?? null;
    const numeric = this.coerceNumber(rsValue);
    if (numeric === null) {
      return this.displayOrDash(rsValue);
    }
    return this.formatNumberWithPrecision(numeric, this.getResultPrecision(materialItem));
  }

  /**
   * Normalizes "<low> to <high>" rendering so every row shares the same dash/quote rules.
   */
  private buildSpanString(low: unknown, high: unknown): string {
    if (this.isValuePresent(low) && this.isValuePresent(high)) {
      return `${low} to ${high}`;
    }
    return '-';
  }

  /**
   * Coerces template-provided values into usable numbers.
   */
  private coerceNumber(value: unknown): number | null {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }
    if (typeof value === 'string' && value.trim().length) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  /**
   * Utility guard so 0/0.0 remain printable while undefined/null become "-".
   */
  private isValuePresent(value: unknown): value is string | number {
    return value !== null && value !== undefined && value !== '';
  }

  /**
   * Same resolution the calculation request uses (see resolveCalLotForRegion in helper.ts and its
   * call in calculate.model.ts) so the displayed lot always matches what a run was actually
   * calculated with. `reportContentItems.calLot` is not populated anywhere in the app and cannot be
   * used for this.
   */
  getCalibratorLotForColumn(materialItem: AssayData | undefined): string {
    const region = materialItem?.reportContentItems?.region;
    if (region !== undefined && this.calibratorLotSnapshot.has(region)) {
      return this.calibratorLotSnapshot.get(region)!;
    }
    return this.resolveCalibratorLotNumber(region);
  }

  /**
   * Run Setup edits (sample types, accessions) feed the lot resolution, but the table must only
   * change when a new calculation response arrives. The lot is resolved once per response here and
   * the template reads this snapshot instead of re-resolving against live run samples.
   */
  private snapshotCalibratorLots(): void {
    this.calibratorLotSnapshot.clear();
    if (!this.calculationResult && !this.calculationAdjustedResult) {
      return;
    }
    (this.reportContent ?? []).forEach(item => {
      const region = (item as { region?: number })?.region;
      if (region !== undefined && !this.calibratorLotSnapshot.has(region)) {
        this.calibratorLotSnapshot.set(region, this.resolveCalibratorLotNumber(region));
      }
    });
  }

  private resolveCalibratorLotNumber(region: number | undefined): string {
    return resolveCalLotForRegion(this.calLots, region, this.runSamples)?.calLotNumber ?? '';
  }

  private getResultPrecision(materialItem: AssayData): number | null {
    return materialItem?.reportContentItems?.userDefinedDecimalPrecision ??
      materialItem?.reportContentItems?.resultDecimalPrecision ??
      null;
  }

  private formatNumberWithPrecision(value: number, precision: number | null): string {
    if (!Number.isFinite(value)) {
      return '-';
    }
    const maxDecimals = Math.min(precision ?? 6, 6);
    const factor = 10 ** maxDecimals;
    const rounded = Math.round(value * factor) / factor;
    const fixed = rounded.toFixed(maxDecimals);
    // Only trim a fractional tail — at precision 0 there is no decimal point, and stripping
    // trailing zeros from a whole number would turn 100 into 1.
    return fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
  }

  /**
   * Initializes assay data based on provided calculation results.
   * @param calculationResult Calculation result object
   * @param label Label for original/adjusted calculations
   */
  /**
   * Builds the AssayData array for either the original or adjusted calculation response.
   * Each report content row is hydrated via precomputed lookup maps so we never
   * re-run expensive `Array.find` calls while iterating large lot lists.
   */
  private createAssayDataSet(
    label: string,
    regressionStatsMap: Map<string, RegressionStats>,
    runStatusMap: Map<string, RunStatus>,
    assaySpecsByRegion: Map<number, Assay_Specs[]>
  ): AssayData[] {
    if (!this.filteredReportContent?.length) {
      return [];
    }

    const result: AssayData[] = [];

    this.filteredReportContent.forEach((reportContentItem) => {
      const regressionStat = regressionStatsMap.get(reportContentItem.reportContentItemId);
      const isMeanColumn = this.isMeanReportItem(reportContentItem.reportContentItemId);
      const calRunStatus = runStatusMap.get(reportContentItem.reportContentItemId);
      const assaySpec = assaySpecsByRegion.get(reportContentItem.region);

      if (regressionStat) {
        let ratioRanges;
        if (regressionStat.ranges.filter(r => r.isDefaultBiasType).length === 0) {
          ratioRanges = regressionStat.ranges.filter(
            // If no default range is set, Ranges 1 & 4 show Ratio bias; middle ranges display Difference bias.
            (range) => range.rangeNo == 1 || range.rangeNo == 4 ? range.biasType === 'Ratio' : range.biasType === 'Difference'
          );

        }
        else {
          ratioRanges = regressionStat.ranges.filter(
            // If there's a default range, choose that one 
            (range) => range.isDefaultBiasType
          );
        }



        if (ratioRanges.length > 0) {
          const resultItem: AssayData = {
            reportContentItems: reportContentItem,
            regressionStats: {
              ...regressionStat,
              ranges: ratioRanges
            },
            runStatus: calRunStatus,
            valueAssigned: label,
            isMeanColumn
          };

          const calibrationCurve = this.buildCalibrationCurve(assaySpec, calRunStatus);
          if (calibrationCurve) {
            resultItem.calibrationCurve = calibrationCurve;
          }
          result.push(resultItem);
          return;
        }
      }

      // If the API did not return regression stats for this lot, mimic the previous behavior by
      // pushing an empty shell for regressionStats — but runStatus and the calibration curve are
      // both independent of regression stats, so they must still be bound here or the Run
      // Validity/Calibration Curve sections for this column silently render blank even though the
      // calculation response has the data.
      const fallbackCalibrationCurve = this.buildCalibrationCurve(assaySpec, calRunStatus);
      result.push({
        reportContentItems: reportContentItem,
        regressionStats: {
          ranges: [],
          reportContentItemId: '',
          numPassSamples: 0,
          numAlertSamples: 0,
          numFailSamples: 0,
          averageBeadCV: 0,
          averageBeadCount: 0,
          averageCountTime: 0,
          percentPositiveAgreement: 0,
          percentPositiveAgreementCI: '',
          percentNegativeAgreement: 0,
          percentNegativeAgreementCI: '',
          numConcordanceSamples: 0,
          numTruePositives: 0,
          numFalsePositives: 0,
          numEQRef: 0,
          numTrueNegatives: 0,
          numFalseNegatives: 0,
          numEQTest: 0,
          numEQ: 0,
          negativeSamplesPASS: 0,
          negativeSamplesFAILED: 0,
          negativeSamplesALERT: 0,
          positiveSamplesPASS: 0,
          positiveSamplesFAILED: 0,
          positiveSamplesAlert: 0,
          cutOffSamplesPASS: 0,
          cutOffSamplesFail: 0,
          CutOffSamplesAlert: 0
        },
        runStatus: calRunStatus,
        ...(fallbackCalibrationCurve && { calibrationCurve: fallbackCalibrationCurve }),
        valueAssigned: label,
        isMeanColumn
      });
    });

    return result;
  }

  /**
   * Builds the calibration curve block for an Assay row from its assay spec and run status.
   * Independent of regression stats, so callers should build it regardless of whether regression
   * stats were available for the row.
   */
  private buildCalibrationCurve(
    assaySpec: Assay_Specs[] | undefined,
    calRunStatus: RunStatus | undefined
  ): AssayDataCalCurve | undefined {
    if (!assaySpec || assaySpec.length === 0) {
      return undefined;
    }
    return {
      assay_range_lower: assaySpec[0].assay_range_lower,
      assay_range_upper: assaySpec[0].assay_range_upper,
      cut_off: assaySpec[0].cut_off,
      result_decimal_precision: assaySpec[0].result_decimal_precision,
      math_model: assaySpec[0].math_model,
      curve_direction: assaySpec[0].curve_direction,
      cal_level_for_cv: assaySpec[0].calibrator_used_for_bead_to_bead_cv,
      monotonic: calRunStatus?.calCurveInformation?.monotonic
    };
  }

  /**
   * Combines original and adjusted calculation results into one dataset.
   * @returns Combined array of AssayData
   */
  getCombinedData() {
    const combined = [
      ...(this.calResponseFilteredData ?? []),
      ...(this.calResponseFilteredAdjustedData ?? [])
    ];
    return combined.sort((left, right) => this.compareAssayData(left, right));
  }
  /**
   * Determines the overall pass/fail/alert status of each range.
   * @returns Array of range numbers and their status
   */
  checkRangeStatus(): { rangeNo: number; status: string }[] {
    const result: { rangeNo: number; status: string }[] = [];
    const rangeStatusMap: { [key: number]: string[] } = {};

    this.calResponseCombinedData.forEach(({ regressionStats }) => {
      regressionStats.ranges.forEach(({ rangeNo, meanBiasResult, SlopeLimitResult, InterceptLimitResult, CorrelationLimitResult, SEELimitResult, limitOfAgreementResult }) => {
        const results = [meanBiasResult, SlopeLimitResult, InterceptLimitResult, CorrelationLimitResult, SEELimitResult, limitOfAgreementResult];

        if (!rangeStatusMap[rangeNo]) rangeStatusMap[rangeNo] = [];
        const filteredResults = results.filter((result): result is string => result !== undefined && result !== null);
        const status = this.resultPassFailAlert(filteredResults);

        rangeStatusMap[rangeNo].push(status);
      });
    });
    Object.entries(rangeStatusMap).forEach(([rangeNo, statuses]) => {
      const meaningful = statuses.filter(status => status && status !== 'NA');
      const status = meaningful.length ? this.resultPassFailAlert(meaningful) : 'NA';
      result.push({
        rangeNo: +rangeNo,
        status
      });
    });

    return result;
  }
  /**
   * Safely retrieves string-type variable from range object.
   * @param materialItem AssayData item
   * @param rangeItem Index of the range
   * @param variableName Key to extract
   * @returns The variable value or null
   */
  getProcessedRangeData(materialItem: AssayData, rangeItem: number, variableName: string): string | null {
    const value = this.getRangeData(materialItem, rangeItem, variableName);
    if (value && typeof value === 'object' && Object.keys(value).length === 0) {
      return null;
    }
    return typeof value === 'string' ? value : null;
  }

  /**
   * Toggles visibility of a single section by index.
   * @param sectionIndex Index of the section
   */
  toggleSectionDisplay(sectionIndex: number) {
    this.sectionVisibility[sectionIndex] = !this.sectionVisibility[sectionIndex];
    this.scheduleEmptyRowPrune();
  }

  /**
   * Expands or collapses all sections.
   * @param isExpand Boolean flag to expand or collapse
   */
  toggleAllSections(isExpand: boolean) {
    this.sectionVisibility = this.sectionVisibility.map(() => isExpand);
    this.scheduleEmptyRowPrune();
  }

  /**
   * A section only renders its rows when the operator has it expanded *and* it has something to
   * show. Sections with no data stay collapsed so an empty panel is never left hanging open.
   * @param sectionIndex Index of the section
   */
  isSectionExpanded(sectionIndex: number): boolean {
    return !!this.sectionVisibility[sectionIndex] && !!this.sectionHasData[sectionIndex];
  }

  /** Recomputes which sections have renderable rows for the columns currently displayed. */
  private refreshSectionDataFlags(): void {
    const flags = new Array<boolean>(SECTION_COUNT).fill(false);
    const items = this.calResponseCombinedData ?? [];

    if (items.length) {
      flags[0] = this.hasSummaryStatusData();
      flags[1] = this.hasRunValidityData(items);
      flags[2] = this.hasCalibrationCurveData(items);
      flags[3] = this.hasCalCurveParameterData(items);
      flags[4] = this.hasControlQcData(items);
      flags[5] = this.hasControlTestSectionData(items);
      (this.ranges ?? []).forEach(rangeItem => {
        flags[RANGE_SECTION_OFFSET + rangeItem] = items.some(item => this.hasRenderableRangeRow(item, rangeItem));
      });
    }

    this.sectionHasData = flags;
  }

  /** Mirrors the empty-cell rule used by `pruneEmptyRows`. */
  private isRenderedValue(text: string | null | undefined): boolean {
    const trimmed = (text ?? '').trim();
    return trimmed.length > 0 && trimmed !== '-';
  }

  private hasSummaryStatusData(): boolean {
    return this.isRenderedValue(this.getPatientFileData('date')) ||
      this.isRenderedValue(this.getPatientFileData('instrumentSerialNo')) ||
      !!this.calLots?.length ||
      !!this.qcLots?.length ||
      SUMMARY_STATUS_FIELDS.some(field => this.shouldRenderReportContentRow(field)) ||
      (this.bulkMaterial ?? []).some(item => this.shouldRenderBulkMaterialRow(item.bulk_material_description));
  }

  private hasRunValidityData(items: AssayData[]): boolean {
    return items.some(item => {
      const runStatus = item?.runStatus;
      const stats = item?.regressionStats;
      const values: unknown[] = [
        runStatus?.kitStatus,
        runStatus?.calibratorSpecStatus,
        runStatus?.patientSampleSpecStatus,
        runStatus?.patientSampleAcceptanceRange,
        runStatus?.patientSamplesRun,
        runStatus?.patientSamplesSpecified,
        runStatus?.calCurveInformation?.calCurveValidity,
        runStatus?.calCurveInformation?.calReplicateValidity,
        runStatus?.controlResultsValidity,
        runStatus?.sampleReplicateCountValidity,
        runStatus?.sampleReplicateCVValidity,
        runStatus?.controlTestStatus,
        runStatus?.numRVsDiscardedAssayAndRvErrors,
        runStatus?.numRVsDiscardedBeadCountErrors,
        runStatus?.numRVsDiscardedCalFailed,
        runStatus?.numControlsDiscardedFailed,
        runStatus?.numRVsDiscardedOutliers,
        runStatus?.numRVsAnalyzed,
        this.runValidityPassFailAlertCount(
          stats?.negativeSamplesPASS || 0, stats?.negativeSamplesFAILED || 0, stats?.negativeSamplesALERT || 0),
        this.runValidityPassFailAlertCount(
          stats?.cutOffSamplesPASS || 0, stats?.cutOffSamplesFail || 0, stats?.CutOffSamplesAlert || 0),
        this.runValidityPassFailAlertCount(
          stats?.positiveSamplesPASS || 0, stats?.positiveSamplesFAILED || 0, stats?.positiveSamplesAlert || 0)
      ];
      return values.some(value => this.isRenderedValue(this.getRunValidityDisplay(item, value)));
    });
  }

  private hasCalibrationCurveData(items: AssayData[]): boolean {
    return items.some(item => {
      const curve = item?.calibrationCurve;
      return [
        curve?.assay_range_lower,
        curve?.assay_range_upper,
        curve?.cut_off,
        curve?.result_decimal_precision,
        curve?.math_model,
        curve?.curve_direction,
        curve?.monotonic
      ].some(value => this.isRenderedValue(this.displayOrDash(value)));
    });
  }

  private hasCalCurveParameterData(items: AssayData[]): boolean {
    return items.some(item => {
      const info = item?.runStatus?.calCurveInformation as Record<string, unknown> | undefined;
      if (this.isRenderedValue(this.displayOrDash(item?.calibrationCurve?.cal_level_for_cv))) {
        return true;
      }
      if (!info) {
        return false;
      }
      return CAL_CURVE_PARAMETER_METRICS.some(metric =>
        CAL_CURVE_PARAMETER_SUFFIXES.some(suffix => this.isRenderedValue(this.displayOrDash(info[`${metric}${suffix}`])))
      );
    });
  }

  private hasControlQcData(items: AssayData[]): boolean {
    if (!this.controlQcLevels.length) {
      return false;
    }
    return items.some(item =>
      this.isRenderedValue(this.getControlLotNumber(item)) ||
      this.controlQcLevels.some(level =>
        this.isRenderedValue(this.getControlBarcode(item, level)) ||
        this.isRenderedValue(this.getControlTestResult(item, level)) ||
        this.isRenderedValue(this.getControlCriteriaDisplay(item, level)) ||
        this.isRenderedValue(this.getControlRange(item, level)) ||
        this.isRenderedValue(this.getControlStatus(item, level))
      )
    );
  }

  private hasControlTestSectionData(items: AssayData[]): boolean {
    if (this.isControlAssignmentRun() || !this.controlTestLots.length || !this.controlTestLevels.length) {
      return false;
    }
    return items.some(item =>
      this.controlTestLots.some(lot =>
        this.isRenderedValue(this.getControlTestLotDisplay(item, lot)) ||
        this.controlTestLevels.some(level =>
          this.isRenderedValue(this.getControlTestBarcode(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestResultValue(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestCriteriaAlert(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestRangeAlert(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestStatusAlert(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestCriteriaPass(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestRangePass(item, lot, level)) ||
          this.isRenderedValue(this.getControlTestStatusPass(item, lot, level))
        )
      )
    );
  }

  get shouldRenderRangeSections(): boolean {
    return this.hasRenderableRangeRowsBeyondBiasDf();
  }

  isControlAssignmentRun(): boolean {
    const raw = (this.runType ?? '').trim();
    if (!raw) {
      return false;
    }
    const normalized = raw.toLowerCase().replace(/[^a-z]/g, '');
    return normalized === 'controlassignment';
  }

  private hasRenderableRangeRowsBeyondBiasDf(): boolean {
    for (const rangeItem of this.ranges ?? []) {
      for (const materialItem of this.calResponseCombinedData ?? []) {
        if (this.hasRenderableRangeRow(materialItem, rangeItem)) {
          return true;
        }
      }
    }
    return false;
  }

  private hasRenderableRangeRow(materialItem: AssayData, rangeItem: number): boolean {
    const checks: Array<() => string | null | undefined> = [
      () => this.formatRangeNumber(materialItem, rangeItem, 'slope'),
      () => this.displayOrDash(this.getRangeData(materialItem, rangeItem, 'slopeCI')),
      () => this.getProcessedRangeData(materialItem, rangeItem, 'SlopeLimitResult') ?? '-',
      () => this.formatRangeSpan(materialItem, rangeItem, 'SlopeLimitLowSpecAlert', 'SlopeLimitHighSpecAlert'),
      () => this.formatRangeSpan(materialItem, rangeItem, 'SlopeLimitLowSpec', 'SlopeLimitHighSpec'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'intercept'),
      () => this.displayOrDash(this.getRangeData(materialItem, rangeItem, 'interceptCI')),
      () => this.getProcessedRangeData(materialItem, rangeItem, 'InterceptLimitResult') ?? '-',
      () => this.formatRangeSpan(materialItem, rangeItem, 'InterceptLimitLowSpecAlert', 'InterceptLimitHighSpecAlert'),
      () => this.formatRangeSpan(materialItem, rangeItem, 'InterceptLimitLowSpec', 'InterceptLimitHighSpec'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'correlation'),
      () => this.getProcessedRangeData(materialItem, rangeItem, 'CorrelationLimitResult') ?? '-',
      () => this.displayOrDash(this.getRangeData(materialItem, rangeItem, 'CorrelationLimitSpecAlert')),
      () => this.displayOrDash(this.getRangeData(materialItem, rangeItem, 'CorrelationLimitSpec')),
      () => this.formatRangeNumber(materialItem, rangeItem, 'see'),
      () => this.getRsquared(materialItem, rangeItem),
      () => this.formatRangeNumber(materialItem, rangeItem, 'meanBias'),
      () => this.displayOrDash(this.getRangeData(materialItem, rangeItem, 'meanBiasCI')),
      () => this.getProcessedRangeData(materialItem, rangeItem, 'meanBiasResult') ?? '-',
      () => this.formatRangeSpan(materialItem, rangeItem, 'meanBiasLowSpecAlert', 'meanBiasHighSpecAlert'),
      () => this.formatRangeSpan(materialItem, rangeItem, 'meanBiasLowSpec', 'meanBiasHighSpec'),
      () => this.getProcessedRangeData(materialItem, rangeItem, 'limitOfAgreementResult') ?? '-',
      () => this.formatRangeNumber(materialItem, rangeItem, 'lowLimitOfAgreement'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'highLimitOfAgreement'),
      () => this.formatRangeSpan(materialItem, rangeItem, 'limitOfAgreementLowSpec', 'limitOfAgreementHighSpec'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'sd'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'se'),
      () => this.getScaledRangeValue(materialItem, rangeItem, 'sd', 1.96),
      () => this.getScaledRangeValue(materialItem, rangeItem, 'se', 1.96),
      () => this.formatRangeNumber(materialItem, rangeItem, 'tStat'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'tCritical'),
      () => this.formatRangeNumber(materialItem, rangeItem, 'p')
    ];

    for (const check of checks) {
      const value = check();
      if (typeof value === 'string') {
        if (value.trim() !== '-' && value.trim().length > 0) {
          return true;
        }
      } else if (value !== null && value !== undefined) {
        return true;
      }
    }
    return false;
  }

  /**
   * Filters data based on selected table columns.
   * @param selectedColumns Array of selected columns
   */
  handleSelectColumns(selectedColumns: ColumnSelection[], userInitiated = true) {
    this.selectedColumns = selectedColumns;
    this.calResponseCombinedData = this.calResponseCombinedBackupData.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
    this.refreshSectionDataFlags();
    if (this.isPrintMode && userInitiated) {
      this.printPagesSnapshot = this.buildPrintPages();
    }
    if (!this.isPrintView && !this.exportMode) {
      this.persistColumnSelection(selectedColumns);
    }
  }
  /**
   * Columns chosen in the print preview bar. Held separately from `tableColumns`, which is
   * rebuilt from the store and always comes back fully selected.
   */
  handlePrintColumnSelection(selectedColumns: ColumnSelection[]): void {
    this.printColumnSelection = selectedColumns;
    this.handleSelectColumns(selectedColumns);
  }

  /** The print selection re-keyed onto freshly rebuilt columns; everything until a choice is made. */
  private printColumnsForRebuild(): ColumnSelection[] {
    return applyPrintColumnSelection(this.tableColumns, this.printColumnSelection);
  }

  /** Identifies the document in the print preview bar, beside the study name. */
  get printContextLine(): string {
    return [
      this.runType,
      this.testSpec ? `Test Spec ${this.testSpec}` : '',
      this.specVersion ? `Spec Rev ${this.specVersion}` : '',
      this.operatorName
    ].filter(Boolean).join(' · ');
  }

  /**
   * Handles menu open status and toggles section display accordingly.
   * Retained for the screens still on the older print menu.
   * @param isMenuOpen Boolean flag indicating menu state
   */
  handleMenuOpenStatus(isMenuOpen: boolean) {
    this.isPrintMode = isMenuOpen;

    if (isMenuOpen) {
      this.printPagesSnapshot = this.buildPrintPages();
      this.toggleAllSections(isMenuOpen);
    }
  }
  /**
   * Formats and returns the current date as a string.
   * @returns Formatted date string
   */
  getDate(): string {
    return this.appUtilityService.formatDate(new Date());
  }
  /**
   * Retrieves a specific field value from patient file metadata.
   * @param key Metadata field key
   * @returns Field value as string or empty
   */
  getPatientFileData<K extends keyof FileMetaData>(key: K): string {
    if (this.patientFileMetadata.length && this.patientFileMetadata[0][key] != null) {
      return this.patientFileMetadata[0][key] as string;
    }
    return '';
  }
  /**
   * Gets measurement units for a given bead region.
   * @param region Bead region number
   * @returns Units as string
   */
  getUnitsByRegion(region: number): string {
    if (!this.panelSpecs || !Array.isArray(this.panelSpecs?.Assay_Specs)) {
      return '';
    }

    const match = this.panelSpecs?.Assay_Specs.find(spec => spec.bead_region === region);
    return match?.units ?? '';
  }
  /**
   * Navigates to the print preview route for assay summary.
   */
  navigateToPrintView() {
    this.router.navigateByUrl(
      `/(print:print/preview-assay-summary)?truncated=${this.printTruncatedReport ? '1' : '0'}`
    );
  }

  shouldHideForTruncatedPrint(label: string): boolean {
    if (!this.isPrintView || !this.printTruncatedReport) {
      return false;
    }
    const normalized = label.trim().toLowerCase();
    return normalized === '# rvs discarded: rv and assay errors' ||
      normalized === '# rvs discarded: bead count errors' ||
      normalized === '# rvs discarded: cal failed' ||
      normalized === '# rvs discarded: outlier' ||
      normalized === '# rvs analyzed';
  }

  shouldHideControlTestDataForTruncatedPrint(): boolean {
    return this.printTruncatedReport && (this.isPrintView || this.isPrintMode);
  }

  shouldRenderRangeRowForTruncatedPrint(label: string): boolean {
    if (!this.isPrintView || !this.printTruncatedReport) {
      return true;
    }
    const normalized = label.trim().toLowerCase();
    return normalized === 'slope' ||
      normalized === 'intercept' ||
      normalized === 'correlation' ||
      normalized === 'bias type' ||
      normalized === 'mean' ||
      normalized === 'limit of agreement p/f/a' ||
      normalized === 'low limits of agreement' ||
      normalized === 'high limits of agreement' ||
      normalized === 'pass/fail spec';
  }

  exportAsWorkbook(): void {
    void this.assayWorkbookExportService.exportWorkbook({
      truncateSummary: this.printTruncatedReport
    });
  }

  /**
   * Whether the assay report can be requested. The backend renders from the
   * saved study, so a draft or never-saved study has nothing to report on — and an
   * edited one would print what was last saved rather than what is on screen.
   *
   * The pending check is the same one the audit trail's Print View makes, for the same
   * reason: both documents state what is on the server. It matters most for the run
   * details appendix and the materials block, which print the worklist and report content
   * items straight from the database, where an unsaved edit is simply not there yet.
   */
  get canDownloadReport(): boolean {
    return !!this.studyName?.trim() && this.isStudySaved && !this.areStudyChangesPending;
  }

  /**
   * Why the report cannot be printed, for the disabled button's tooltip. The two reasons
   * are fixed differently — one needs a first save, the other needs the edits saved — so
   * the button says which rather than leaving the operator to guess.
   */
  get reportButtonTooltip(): string {
    if (this.canDownloadReport) {
      return '';
    }
    return this.isStudySaved && this.areStudyChangesPending
      ? 'Save the study to print its report — it has unsaved changes'
      : 'Save the study to print its report';
  }

  /**
   * Whether to offer the unadjusted/adjusted choice. Both halves have to exist for there
   * to be anything to choose between — with one, the report is that one either way.
   */
  private get offerResultBasis(): boolean {
    return !!this.calculationResult && !!this.calculationAdjustedResult;
  }

  /**
   * Whether to ask for the material and batch the sheets are filed against, and whether
   * the report can be printed without them.
   *
   * RMQC sheets are filed against a material the study does not record, so the server
   * refuses the report without them. PreBulk states them when the operator has them and
   * prints without them when not. No other workflow's footer names a material.
   */
  private get reportPaginationInfo(): 'required' | 'optional' | undefined {
    if (isRmqcRunType(this.runType)) {
      return 'required';
    }
    return isPreBulkRunType(this.runType) ? 'optional' : undefined;
  }

  /**
   * Downloads the assay report as a PDF rendered by the backend.
   * Unlike Print View, the document layout is produced server side, so the
   * output does not depend on the browser or printer settings.
   *
   * Asks which columns to include first. That choice applies to the report only:
   * it never touches the on-screen table or the persisted column selection.
   */
  downloadReport(): void {
    // Sent as stored, not trimmed: the server matches run_name exactly, and a study
    // saved with surrounding whitespace would otherwise come back 404.
    const runName = this.studyName;
    if (!this.canDownloadReport) {
      // Worth saying rather than doing nothing: the button reads the same whether the
      // study was never saved or merely edited since, and the second is the case an
      // operator can fix in one step. Worded as the audit trail's Print View words it.
      if (this.isStudySaved && this.areStudyChangesPending) {
        this.appUtilityService.displayAlertDialog({
          title: 'UNSAVED CHANGES',
          message: 'This study has unsaved changes. The report is rendered from what has '
            + 'been saved to the server, so printing now would produce a document that does '
            + 'not match the study on screen. Save the study, then print the report.',
          okText: 'OK',
          dismissText: '',
          okAction: () => { },
          dismissAction: () => { }
        } as CommonDialogData);
      }
      return;
    }

    const columns = this.buildReportColumnChoices();
    const offerResultBasis = this.offerResultBasis;
    // RMQC footers name the material and batch and the server refuses the report without
    // them; PreBulk prints them when given and without them when not.
    const paginationInfo = this.reportPaginationInfo;
    // Every workflow but RMQC and Stability appends the worklist and report items. RMQC's
    // runsheet already states its materials in its own materials block.
    const runDetailsDefault = !isRmqcRunType(this.runType) && !isStabilityRunType(this.runType);

    this.dialog
      .open<PrintColumnSelectionDialogComponent, PrintColumnSelectionDialogData, PrintColumnSelectionResult | undefined>(
        PrintColumnSelectionDialogComponent,
        {
          panelClass: 'bpx-dialog-container',
          data: {
            columns,
            mode: 'kitLotInstrumentBead',
            offerResultBasis,
            paginationInfo,
            runDetailsDefault
          }
        }
      )
      .afterClosed()
      .pipe(
        take(1),
        switchMap(chosen => this.buildReportRequest(runName, chosen)),
        takeUntil(this.destroy$)
      )
      .subscribe(this.buildReportObserver(runName));
  }

  /**
   * The columns the report can be filtered by: one row per report content item.
   *
   * Keyed by reportContentItemId rather than by the on-screen column key, because
   * a loaded study carries the run id in `runId` instead of the instrument, so the
   * instrument is only recoverable from the id itself.
   */
  private buildReportColumnChoices(): ColumnSelection[] {
    const reportContent = this.reportContent ?? [];
    return reportContent
      .filter(item => !!item?.reportContentItemId && !this.isMeanReportItem(item.reportContentItemId))
      .slice()
      .sort((left, right) => this.compareReportContentItems(left, right))
      .map(item => ({
        columnKey: item.reportContentItemId,
        columnName: item.lot,
        kitLot: item.lot,
        instrumentId: this.extractInstrumentId(item),
        beadRegion: item.region,
        // Start from what the table is already showing, without sharing that state.
        isSelected: (this.selectedColumns ?? []).some(column => this.matchesSelection(column, item))
      }));
  }

  /**
   * Pulls the instrument out of a report content item id, which both the frontend
   * and the backend compose as `<kit lot><bead region>_<instrument id>`.
   */
  private extractInstrumentId(item: ReportContentItem): string {
    const id = item?.reportContentItemId ?? '';
    const separatorIndex = id.lastIndexOf('_');
    if (separatorIndex >= 0 && separatorIndex < id.length - 1) {
      return id.slice(separatorIndex + 1);
    }
    return `${item?.runId ?? ''}`;
  }

  /**
   * Turns the dialog's outcome into the request to make, or nothing at all when
   * the user cancelled or left every column unchecked.
   */
  private buildReportRequest(runName: string, chosen: PrintColumnSelectionResult | undefined): Observable<Blob> {
    if (!chosen) {
      return EMPTY;
    }

    const { includeUnadjusted, includeAdjusted } = chosen;
    // A study with no columns to choose between opens the dialog for the halves alone,
    // so there is no selection to narrow by and the server reports every column.
    if (!chosen.columns.length) {
      return this.requestReport(runName, null, includeUnadjusted, includeAdjusted, chosen);
    }

    const selectedIds = this.collectSelectedReportContentItemIds(chosen.columns);
    if (!selectedIds.length) {
      // Apply is not disabled on an empty selection, so say why nothing happened.
      this.appUtilityService.displayAlertDialog({
        title: 'Print Report',
        message: 'Select at least one column to include in the report.',
        okText: 'OK',
        dismissText: '',
        okAction: () => { },
        dismissAction: () => { }
      } as CommonDialogData);
      return EMPTY;
    }

    const reportableIds = this.collectReportableItemIds();
    const isEverythingSelected = selectedIds.length >= reportableIds.length;
    return this.requestReport(
      runName, isEverythingSelected ? null : selectedIds, includeUnadjusted, includeAdjusted, chosen
    );
  }

  /**
   * The report content item ids the user kept checked. The dialog rows carry the id
   * as their columnKey, so this is a direct lookup; intersecting with the reportable
   * ids drops anything the report could not have printed anyway.
   */
  private collectSelectedReportContentItemIds(columns: ColumnSelection[]): string[] {
    const checkedIds = new Set(
      columns
        .filter(column => column.isSelected)
        .map(column => column.columnKey ?? '')
        .filter(columnKey => columnKey.length > 0)
    );
    return this.collectReportableItemIds().filter(id => checkedIds.has(id));
  }

  /** Every id the report could include, so "all selected" can send no filter at all. */
  private collectReportableItemIds(): string[] {
    const ids = new Set<string>();
    (this.reportContent ?? []).forEach(item => {
      const id = item?.reportContentItemId;
      if (id && !this.isMeanReportItem(id)) {
        ids.add(id);
      }
    });
    return Array.from(ids);
  }

  /**
   * Requests the PDF. A null id list omits the filter entirely, which keeps the
   * common "print everything" case off a query string that grows with the panel.
   */
  private requestReport(
    runName: string,
    selectedIds: string[] | null,
    includeUnadjusted: boolean,
    includeAdjusted: boolean,
    chosen?: Pick<PrintColumnSelectionResult, 'materialNumber' | 'batchNumber' | 'includeRunDetails'>
  ): Observable<Blob> {
    const params = [`run_name=${encodeURIComponent(runName)}`];
    if (selectedIds?.length) {
      params.push(`report_content_item_ids=${encodeURIComponent(JSON.stringify(selectedIds))}`);
    }
    // Omitted when both halves are wanted: that is the server's own default, so the
    // ordinary request keeps the query string it had before halves could be chosen.
    if (!(includeUnadjusted && includeAdjusted)) {
      params.push(`include_unadjusted=${includeUnadjusted}`);
      params.push(`include_adjusted=${includeAdjusted}`);
    }
    // Only RMQC and PreBulk collect these, and the server requires them for RMQC; omitted
    // otherwise so every other workflow's request is the one it was before the footer
    // carried them, and omitted for PreBulk when the operator left them blank.
    if (chosen?.materialNumber) {
      params.push(`material_number=${encodeURIComponent(chosen.materialNumber)}`);
    }
    if (chosen?.batchNumber) {
      params.push(`batch_number=${encodeURIComponent(chosen.batchNumber)}`);
    }
    // Omitted when off, which is the server's own default, so a report without the
    // appendix asks for exactly what it asked for before the appendix existed.
    if (chosen?.includeRunDetails) {
      params.push('include_run_details=true');
    }
    const url = `${this.appUtilityService.getAPI(apiPaths.reports.exportReport)}?${params.join('&')}`;
    // The server renders the PDF in headless Chromium, so this takes seconds with no
    // other sign of progress; the header banner is the only feedback the user gets.
    // Started here so it never goes up while the column dialog is still open.
    this.reportDownloadStatus.start();
    return this.appUtilityService.getBlob(url).pipe(
      take(1),
      finalize(() => this.reportDownloadStatus.stop())
    );
  }

  private buildReportObserver(runName: string) {
    return {
      next: (blob: Blob) => {
        saveBlobAsFile(blob, `${runName}_report.pdf`);
        this.logReportPrintAuditTrail(runName);
      },
      error: (error: Error) => {
        this.appUtilityService.displayAlertDialog({
          title: 'Print Report Error',
          message: error?.message || this.appUtilityService.errorMessages.serviceFailure,
          okText: 'OK',
          dismissText: '',
          okAction: () => { },
          dismissAction: () => { }
        } as CommonDialogData);
      }
    };
  }

  /**
   * Records a Print action against the study, matching how Print View is audited.
   * Failures are swallowed so a logging outage never loses the downloaded report.
   */
  private logReportPrintAuditTrail(runName: string): void {
    const payload = {
      run_name: runName,
      action: 'Print',
      comment: 'Assay Summary Report'
    };
    const url = this.appUtilityService.getAPI(apiPaths.study.auditTrail);
    this.appUtilityService.post<typeof payload, { audit_trail: AuditTrailEntry[] }>(url, payload, true)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe(response => {
        if (response?.audit_trail) {
          this.store.dispatch(setAuditTrail({ auditTrail: response.audit_trail }));
        }
      });
  }

  /**
   * @description Transforms a given column name to camelCase format.
   * Typically used to normalize variable names for consistent display or access.
   * 
   * @param column The original column name (e.g., with underscores or spaces).
   * @returns The transformed column name in camelCase.
   */
  transformedVariableColumns(column: string) {
    return transformToCamelCase(column);
  }

  displayOrDash(value: unknown): string {
    if (typeof value === 'string' || typeof value === 'number' || value === null || value === undefined) {
      return displayOrDash(value);
    }
    return '-';
  }

  formatSpecRange(min: unknown, max: unknown): string {
    return formatSpecRange(min, max);
  }

  isMeanColumnAssay(materialItem: AssayData | undefined): boolean {
    if (!materialItem) {
      return false;
    }
    return materialItem.isMeanColumn ?? this.isMeanReportItem(materialItem.reportContentItems?.reportContentItemId);
  }

  private applyStoredColumnSelection(columnKeys: string[] | null | undefined): void {
    if (!this.tableColumns?.length) {
      this.selectedColumns = [];
      return;
    }
    const selectionSet = new Set((columnKeys ?? []).filter(Boolean));
    const hasMatches = this.tableColumns.some(column =>
      selectionSet.has(column.columnKey ?? column.columnName ?? '')
    );
    const useDefault = selectionSet.size === 0 || !hasMatches;
    this.tableColumns = this.tableColumns.map(column => ({
      ...column,
      isSelected: useDefault ? true : selectionSet.has(column.columnKey ?? column.columnName ?? '')
    }));
    this.selectedColumns = this.tableColumns;
    this.calResponseCombinedData = this.calResponseCombinedBackupData.filter((item) =>
      this.selectedColumns.some((column) => this.matchesSelection(column, item.reportContentItems))
    );
  }

  private persistColumnSelection(columns: ColumnSelection[]): void {
    const columnKeys = (columns ?? [])
      .filter(column => column.isSelected)
      .map(column => column.columnKey ?? column.columnName ?? '')
      .filter(key => key.length > 0);
    this.store.dispatch(setAssaySummaryColumnSelection({ columnKeys }));
  }

  getRunValidityDisplay(materialItem: AssayData | undefined, value: unknown): string {
    if (this.isMeanColumnAssay(materialItem)) {
      return '-';
    }
    return this.displayOrDash(value);
  }

  getRunValidityStatusClass(materialItem: AssayData | undefined, value: string | undefined | null): string {
    if (this.isMeanColumnAssay(materialItem)) {
      return '';
    }
    return this.getStatusClassFromResult(value);
  }

  getControlBarcode(materialItem: AssayData, levelNo: number): string {
    const vial = this.getControlVial(materialItem, levelNo);
    return this.displayOrDash(vial?.barcode);
  }

  getControlLotNumber(materialItem: AssayData): string {
    const lot = this.getControlQcEntry(materialItem)?.lotNumber;
    return this.displayOrDash(lot);
  }

  getControlTestResult(materialItem: AssayData, levelNo: number): string {
    const vial = this.getControlVial(materialItem, levelNo);
    return this.displayOrDash(vial?.testMean);
  }

  getControlCriteriaDisplay(materialItem: AssayData, levelNo: number): string {
    const vial = this.getControlVial(materialItem, levelNo);
    if (!vial || vial.sdSpecPass === undefined || vial.sdSpecPass === null || vial.numSDsPass === undefined || vial.numSDsPass === null) {
      return '-';
    }

    const magnitude = Number(vial.sdSpecPass) * Number(vial.numSDsPass);
    if (!isFinite(magnitude)) {
      return '-';
    }

    if (vial.sdSpecType === '%') {
      return `${formatComputedNumber(magnitude * 100)}%`;
    }

    return formatComputedNumber(magnitude);
  }

  getControlRange(materialItem: AssayData, levelNo: number): string {
    const vial = this.getControlVial(materialItem, levelNo);
    const lower = this.displayOrDash(vial?.lowerLimitPass);
    const upper = this.displayOrDash(vial?.upperLimitPass);

    if (lower === '-' && upper === '-') {
      return '-';
    }

    return `${lower} to ${upper}`;
  }

  getControlStatus(materialItem: AssayData, levelNo: number): string {
    const vial = this.getControlVial(materialItem, levelNo);
    return this.formatStatusText(vial?.status);
  }

  getControlTestLotDisplay(materialItem: AssayData, lot: string): string {
    const control = this.getControlTestControl(materialItem, lot);
    return control ? this.displayOrDash(control.lotNumber) : '-';
  }

  getControlTestBarcode(materialItem: AssayData, lot: string, levelNo: number): string {
    const vial = this.getControlTestVial(materialItem, lot, levelNo);
    return this.displayOrDash(vial?.barcode);
  }

  getControlTestResultValue(materialItem: AssayData, lot: string, levelNo: number): string {
    const vial = this.getControlTestVial(materialItem, lot, levelNo);
    return this.displayOrDash(vial?.testMean);
  }

  getControlTestCriteriaAlert(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestCriteriaValue(materialItem, lot, levelNo, 'alert');
  }

  getControlTestCriteriaPass(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestCriteriaValue(materialItem, lot, levelNo, 'pass');
  }

  getControlTestRangeAlert(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestRangeValue(materialItem, lot, levelNo, 'alert');
  }

  getControlTestRangePass(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestRangeValue(materialItem, lot, levelNo, 'pass');
  }

  getControlTestStatusAlert(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestStatusValue(materialItem, lot, levelNo, 'alert');
  }

  getControlTestStatusPass(materialItem: AssayData, lot: string, levelNo: number): string {
    return this.getControlTestStatusValue(materialItem, lot, levelNo, 'pass');
  }

  private appendMeanColumnsToReportContent(
    reportItems: ReportContentItem[],
    originalResult: CalculationResult | null | undefined,
    adjustedResult: CalculationResult | null | undefined
  ): ReportContentItem[] {
    if (!reportItems?.length) {
      return [];
    }

    if (reportItems.some(item => this.isMeanReportItem(item?.reportContentItemId))) {
      return [...reportItems];
    }

    const meanIds = this.collectAvailableMeanIds([originalResult, adjustedResult]);
    if (!meanIds.size) {
      return reportItems;
    }

    const useKitRegionIds = this.shouldUseKitRegionMeanIds(reportItems, meanIds);
    const insertedMeanIds = new Set<string>();
    const insertedRegions = new Set<number>();
    const result: ReportContentItem[] = [];

    for (let index = 0; index < reportItems.length; index++) {
      const item = reportItems[index];
      result.push(item);

      const regionValue = this.parseRegionValue(item.region);
      if (!Number.isFinite(regionValue)) {
        continue;
      }

      const next = reportItems[index + 1];
      const nextRegionValue = this.parseRegionValue(next?.region);
      const sameRegion = Number.isFinite(nextRegionValue) && regionValue === nextRegionValue;
      const sameKit = (item.lot ?? '') === (next?.lot ?? '');

      if (useKitRegionIds) {
        if (sameRegion && sameKit) {
          continue;
        }
        const meanId = this.buildMeanId(item, regionValue);
        if (!meanId || !meanIds.has(meanId) || insertedMeanIds.has(meanId)) {
          continue;
        }
        const meanColumn = this.createMeanReportContentItem(item, meanId);
        if (meanColumn) {
          insertedMeanIds.add(meanId);
          result.push(meanColumn);
        }
        continue;
      }

      if (sameRegion) {
        continue;
      }
      if (insertedRegions.has(regionValue)) {
        continue;
      }
      const legacyMeanId = this.buildLegacyMeanId(regionValue);
      if (!meanIds.has(legacyMeanId)) {
        continue;
      }
      const meanColumn = this.createMeanReportContentItem(item, legacyMeanId);
      if (meanColumn) {
        insertedRegions.add(regionValue);
        result.push(meanColumn);
      }
    }

    return result;
  }

  private collectAvailableMeanIds(calculations: (CalculationResult | null | undefined)[]): Set<string> {
    const ids = new Set<string>();
    calculations.forEach(calculation => {
      calculation?.runStatus?.forEach(status => {
        const reportContentItemId = status?.reportContentItemId;
        if (this.isMeanReportItem(reportContentItemId)) {
          ids.add(reportContentItemId as string);
        }
      });
    });
    return ids;
  }

  private shouldUseKitRegionMeanIds(reportItems: ReportContentItem[], meanIds: Set<string>): boolean {
    for (const item of reportItems) {
      const regionValue = this.parseRegionValue(item.region);
      if (!Number.isFinite(regionValue)) {
        continue;
      }
      const candidate = this.buildMeanId(item, regionValue);
      if (candidate && meanIds.has(candidate)) {
        return true;
      }
    }
    return false;
  }

  private buildMeanId(item: ReportContentItem, regionValue: number): string | null {
    const kit = `${item.lot ?? ''}`.trim();
    if (!kit) {
      return null;
    }
    return `Mean_${kit}${regionValue}`;
  }

  private buildLegacyMeanId(regionValue: number): string {
    return `Mean_${regionValue}`;
  }

  private createMeanReportContentItem(template: ReportContentItem, reportContentItemId: string): ReportContentItem | null {
    const meanColumn: ReportContentItem = {
      ...template,
      reportContentItemId,
      runId: 'Mean Values'
    };
    return meanColumn;
  }

  private isMeanReportItem(reportContentItemId: string | undefined): boolean {
    return !!reportContentItemId && reportContentItemId.startsWith('Mean_');
  }

  private setupHeaderHeightObserver(): void {
    const scrollEl = this.assaySummaryScrollRef?.nativeElement;
    const tableEl = this.assaySummaryTableRef?.nativeElement;
    if (!scrollEl || !tableEl) {
      return;
    }

    const headerEl = tableEl.querySelector('thead');
    if (!headerEl) {
      return;
    }

    const updateHeaderHeight = () => {
      const height = headerEl.getBoundingClientRect().height;
      if (height > 0) {
        scrollEl.style.setProperty('--assay-summary-header-height', `${Math.ceil(height)}px`);
      }
    };

    updateHeaderHeight();

    if (typeof ResizeObserver !== 'undefined') {
      this.headerResizeObserver = new ResizeObserver(() => updateHeaderHeight());
      this.headerResizeObserver.observe(headerEl);
    } else {
      this.resizeFallbackListener = () => updateHeaderHeight();
      window.addEventListener('resize', this.resizeFallbackListener);
    }
  }

  /**
   * @description Angular lifecycle hook called when the component is destroyed.
   * Cleans up subscriptions by completing the destroy$ subject to prevent memory leaks.
   */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.headerResizeObserver?.disconnect();
    if (this.resizeFallbackListener) {
      window.removeEventListener('resize', this.resizeFallbackListener);
      this.resizeFallbackListener = null;
    }
  }

  /**
   * Recomputes the combined Assay tables whenever the upstream NgRx slices change.
   * This method memoizes all heavy lookups (panel specs, regression stats, run status),
   * reuses them for both the original and adjusted datasets, and then refreshes the
   * derived summary metrics (range status, run validity, etc.).
   */
  private buildAssayDataSets(): void {
    if (!this.filteredReportContent?.length) {
      this.calResponseFilteredData = [];
      this.calResponseFilteredAdjustedData = [];
      this.calResponseCombinedData = [];
      this.calResponseCombinedBackupData = [];
      this.regressionRangeStatus = [];
      this.calCurveParameterStatus = 'NA';
      this.runValidityStatus = 'NA';
      this.calibrationCurveSectionStatus = 'NA';
      this.controlQcSectionStatus = 'NA';
      this.controlTestSectionStatus = 'NA';
      this.originalControlQcMap.clear();
      this.adjustedControlQcMap.clear();
      this.originalControlTestMap.clear();
      this.adjustedControlTestMap.clear();
      this.controlQcLevels = [];
      this.controlTestLevels = [];
      this.controlTestLots = [];
      return;
    }

    this.originalControlQcMap = this.getControlQcMap(this.calculationResult?.controls);
    this.adjustedControlQcMap = this.getControlQcMap(this.calculationAdjustedResult?.controls);
    this.controlQcLevels = this.computeControlLevelNumbers();
    this.originalControlTestMap = this.getControlTestMap(this.calculationResult?.controls);
    this.adjustedControlTestMap = this.getControlTestMap(this.calculationAdjustedResult?.controls);
    this.controlTestLevels = this.computeControlTestLevelNumbers();

    // Pre-index the expensive arrays so each Assay row can be hydrated via O(1) lookups instead of repeated .find calls.
    const assaySpecsByRegion = this.getAssaySpecsByRegion(this.panelSpecs?.Assay_Specs ?? []);
    const originalRegressionMap = this.getRegressionStatsMap(this.calculationResult);
    const originalRunStatusMap = this.getRunStatusMap(this.calculationResult);

    // A Final run has no unadjusted baseline, so no unadjusted columns are shown.
    this.calResponseFilteredData = this.isFinal ? [] : this.createAssayDataSet(
      'OriginalCalculate',
      originalRegressionMap,
      originalRunStatusMap,
      assaySpecsByRegion
    );

    if (this.calculationAdjustedResult) {
      const adjustedRegressionMap = this.getRegressionStatsMap(this.calculationAdjustedResult);
      const adjustedRunStatusMap = this.getRunStatusMap(this.calculationAdjustedResult);
      this.calResponseFilteredAdjustedData = this.createAssayDataSet(
        'AdjustedCalculate',
        adjustedRegressionMap,
        adjustedRunStatusMap,
        assaySpecsByRegion
      );
    } else {
      this.calResponseFilteredAdjustedData = [];
    }

    this.calResponseCombinedData = this.calResponseFilteredAdjustedData.length > 0
      ? this.getCombinedData()
      : this.calResponseFilteredData;

    this.calResponseCombinedBackupData = this.calResponseCombinedData;
    this.controlTestLots = this.computeControlTestLotOrder();
    this.regressionRangeStatus = this.checkRangeStatus();
    this.calibrationCurveSectionStatus = this.computeCalibrationCurveSectionStatus();
    this.calCurveParameterStatus = this.computeCalCurveParameterSectionStatus();
    this.runValidityStatus = deriveRunValidityStatus(this.calculationResult ?? null, this.calculationAdjustedResult ?? null);
    this.controlQcSectionStatus = this.computeControlQcSectionStatus();
    this.controlTestSectionStatus = this.computeControlTestSectionStatus();
  }

  private buildTableColumns(): ColumnSelection[] {
    const selections = new Map<string, ColumnSelection>();
    const reportContent = this.reportContent ?? [];
    reportContent.forEach(item => {
      const kitLot = item.lot;
      const instrumentId = item.runId;
      const beadRegion = item.region;
      const columnKey = this.buildColumnKey(kitLot, instrumentId, beadRegion);
      if (!selections.has(columnKey)) {
        selections.set(columnKey, {
          columnName: kitLot,
          columnKey,
          kitLot,
          instrumentId,
          beadRegion,
          isSelected: true
        });
      }
    });

    const orderedReportContent = [...reportContent].sort((a, b) => this.compareReportContentItems(a, b));
    const orderByKey = new Map<string, number>();
    orderedReportContent.forEach((item, index) => {
      const key = this.buildColumnKey(item.lot, item.runId, item.region);
      if (!orderByKey.has(key)) {
        orderByKey.set(key, index);
      }
    });

    return Array.from(selections.values()).sort((a, b) => {
      const aOrder = orderByKey.get(a.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      const bOrder = orderByKey.get(b.columnKey ?? '') ?? Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });
  }

  private matchesSelection(column: ColumnSelection, reportContentItem: ReportContentItem): boolean {
    if (!column.isSelected) {
      return false;
    }

    if (!column.columnKey && !column.instrumentId && column.beadRegion === undefined) {
      return column.columnName === reportContentItem.lot;
    }

    const key = this.buildColumnKey(reportContentItem.lot, reportContentItem.runId, reportContentItem.region);
    return (column.columnKey ?? column.columnName) === key;
  }

  private buildColumnKey(kitLot: string | undefined, instrumentId: string | undefined, beadRegion: number | undefined): string {
    return `${kitLot ?? ''}||${instrumentId ?? ''}||${beadRegion ?? ''}`;
  }

  private compareReportContentItems(a: ReportContentItem, b: ReportContentItem): number {
    const baseCompare = this.compareReportContentBase(a, b);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustmentCompare =
      this.getReportContentAdjustmentRank(a) - this.getReportContentAdjustmentRank(b);
    if (adjustmentCompare !== 0) {
      return adjustmentCompare;
    }

    const runCompare = (a.runId ?? '').localeCompare(b.runId ?? '');
    if (runCompare !== 0) {
      return runCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareReportContentBase(a: ReportContentItem, b: ReportContentItem): number {
    const regionCompare = this.compareBeadRegions(a.region, b.region);
    if (regionCompare !== 0) {
      return regionCompare;
    }

    const refLot = (this.refKitLot ?? '').trim();
    const aLot = (a.lot ?? '');
    const bLot = (b.lot ?? '');
    if (refLot) {
      const aIsRef = aLot === refLot;
      const bIsRef = bLot === refLot;
      if (aIsRef !== bIsRef) {
        return aIsRef ? -1 : 1;
      }
    }
    const kitCompare = aLot.localeCompare(bLot);
    if (kitCompare !== 0) {
      return kitCompare;
    }

    return (a.assay ?? '').localeCompare(b.assay ?? '');
  }

  private compareAssayData(left: AssayData, right: AssayData): number {
    const baseCompare = this.compareReportContentBase(left.reportContentItems, right.reportContentItems);
    if (baseCompare !== 0) {
      return baseCompare;
    }

    const adjustCompare = this.getAssayAdjustmentRank(left) - this.getAssayAdjustmentRank(right);
    if (adjustCompare !== 0) {
      return adjustCompare;
    }

    const runCompareWithMean = this.compareRunIdWithMeanLast(left, right);
    if (runCompareWithMean !== 0) {
      return runCompareWithMean;
    }

    return 0;
  }

  private compareRunIdWithMeanLast(left: AssayData, right: AssayData): number {
    const leftIsMean = left.isMeanColumn || this.isMeanReportItem(left.reportContentItems.reportContentItemId);
    const rightIsMean = right.isMeanColumn || this.isMeanReportItem(right.reportContentItems.reportContentItemId);
    if (leftIsMean !== rightIsMean) {
      return leftIsMean ? 1 : -1;
    }
    return (left.reportContentItems.runId ?? '').localeCompare(right.reportContentItems.runId ?? '');
  }

  private getAssayAdjustmentRank(item: AssayData): number {
    return item.valueAssigned === 'AdjustedCalculate' ? 1 : 0;
  }

  private getReportContentAdjustmentRank(item: ReportContentItem): number {
    return item?.['valueAssigned'] === 'AdjustedCalculate' ? 1 : 0;
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.parseRegionValue(left);
    const rightRegion = this.parseRegionValue(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private parseRegionValue(region: string | number | undefined): number {
    if (typeof region === 'number') {
      return Number.isNaN(region) ? 0 : region;
    }
    const parsed = Number(region);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Creates a `reportContentItemId -> regressionStats` map so we can hydrate rows in O(1).
   */
  private getRegressionStatsMap(calculationResult: CalculationResult | null | undefined): Map<string, RegressionStats> {
    if (!calculationResult) {
      return new Map();
    }
    const cached = this.regressionStatsMapCache.get(calculationResult);
    if (cached) {
      return cached;
    }
    const map = this.indexRegressionStatsFromResult(calculationResult);
    this.regressionStatsMapCache.set(calculationResult, map);
    return map;
  }

  private getRunStatusMap(calculationResult: CalculationResult | null | undefined): Map<string, RunStatus> {
    if (!calculationResult) {
      return new Map();
    }
    const cached = this.runStatusMapCache.get(calculationResult);
    if (cached) {
      return cached;
    }
    const map = this.indexRunStatusFromResult(calculationResult);
    this.runStatusMapCache.set(calculationResult, map);
    return map;
  }

  private getAssaySpecsByRegion(assaySpecs: Assay_Specs[] | undefined): Map<number, Assay_Specs[]> {
    if (!assaySpecs || assaySpecs.length === 0) {
      return new Map();
    }
    const cached = this.assaySpecsByRegionCache.get(assaySpecs);
    if (cached) {
      return cached;
    }
    const map = this.indexAssaySpecsByRegion(assaySpecs);
    this.assaySpecsByRegionCache.set(assaySpecs, map);
    return map;
  }

  private getControlQcMap(controls: Controls[] | null | undefined): Map<string, QCControl> {
    if (!controls || controls.length === 0) {
      return new Map();
    }
    const cached = this.controlQcMapCache.get(controls);
    if (cached) {
      return cached;
    }
    const map = this.buildControlQcMap(controls);
    this.controlQcMapCache.set(controls, map);
    return map;
  }

  private getControlTestMap(controls: Controls[] | null | undefined): Map<string, QCControl[]> {
    if (!controls || controls.length === 0) {
      return new Map();
    }
    const cached = this.controlTestMapCache.get(controls);
    if (cached) {
      return cached;
    }
    const map = this.buildControlTestMap(controls);
    this.controlTestMapCache.set(controls, map);
    return map;
  }

  private indexRegressionStatsFromResult(calculationResult: CalculationResult | null | undefined): Map<string, RegressionStats> {
    const map = new Map<string, RegressionStats>();
    calculationResult?.regressionStats?.forEach(stat => map.set(stat.reportContentItemId, stat));
    return map;
  }

  /**
   * Creates a `reportContentItemId -> runStatus` map for the same O(1) hydration behavior.
   */
  private indexRunStatusFromResult(calculationResult: CalculationResult | null | undefined): Map<string, RunStatus> {
    const map = new Map<string, RunStatus>();
    calculationResult?.runStatus?.forEach(runStatus => map.set(runStatus.reportContentItemId, runStatus));
    return map;
  }

  /**
   * Groups assay specs by bead region because multiple specs can share a bead and
   * each Assay row needs to know which calibration values to display.
   */
  private indexAssaySpecsByRegion(assaySpecs: Assay_Specs[] | undefined): Map<number, Assay_Specs[]> {
    const map = new Map<number, Assay_Specs[]>();
    assaySpecs?.forEach(spec => {
      const bucket = map.get(spec.bead_region);
      if (bucket) {
        bucket.push(spec);
      } else {
        map.set(spec.bead_region, [spec]);
      }
    });
    return map;
  }

  private buildControlQcMap(controls: Controls[] | null | undefined): Map<string, QCControl> {
    const map = new Map<string, QCControl>();
    controls?.forEach(control => {
      if (!control?.reportContentItemId || !Array.isArray(control?.qc)) {
        return;
      }

      const qcEntry = control.qc.find(qc => Array.isArray(qc?.vials) && qc.vials.length > 0);
      if (qcEntry) {
        map.set(control.reportContentItemId, qcEntry);
      }
    });
    return map;
  }

  private computeControlLevelNumbers(): number[] {
    const levelSet = new Set<number>();
    const collectLevels = (qcMap: Map<string, QCControl>) => {
      qcMap.forEach(control => {
        control?.vials?.forEach(vial => {
          const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
          if (!Number.isNaN(level)) {
            levelSet.add(level);
          }
        });
      });
    };

    collectLevels(this.originalControlQcMap);
    collectLevels(this.adjustedControlQcMap);

    return Array.from(levelSet).sort((a, b) => a - b);
  }

  private getControlQcEntry(materialItem: AssayData | undefined): QCControl | undefined {
    const key = materialItem?.reportContentItems?.reportContentItemId;
    if (!key) {
      return undefined;
    }

    const primaryMap = materialItem?.valueAssigned === 'AdjustedCalculate'
      ? this.adjustedControlQcMap
      : this.originalControlQcMap;
    let qcEntry = primaryMap.get(key);

    if (!qcEntry) {
      qcEntry = this.originalControlQcMap.get(key) ?? this.adjustedControlQcMap.get(key);
    }

    return qcEntry;
  }

  private getControlVial(materialItem: AssayData, levelNo: number): Vial | undefined {
    return this.getControlQcEntry(materialItem)?.vials?.find(vial => {
      const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
      return level === levelNo;
    });
  }

  private formatStatusText(status: string | undefined | null): string {
    if (status === null || status === undefined) {
      return '-';
    }

    const formatted = this.displayOrDash(status);
    return formatted === '-' ? '-' : formatted.toUpperCase();
  }

  private buildControlTestMap(controls: Controls[] | null | undefined): Map<string, QCControl[]> {
    const map = new Map<string, QCControl[]>();
    controls?.forEach(control => {
      if (!control?.reportContentItemId || !Array.isArray(control?.test)) {
        return;
      }

      const testEntries = control.test.filter(testControl => Array.isArray(testControl?.vials) && testControl.vials.length > 0);
      if (testEntries.length) {
        map.set(control.reportContentItemId, testEntries);
      }
    });
    return map;
  }

  private computeControlTestLevelNumbers(): number[] {
    const levelSet = new Set<number>();
    const collectLevels = (testMap: Map<string, QCControl[]>) => {
      testMap.forEach(testControls => {
        testControls?.forEach(control => {
          control?.vials?.forEach(vial => {
            const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
            if (!Number.isNaN(level)) {
              levelSet.add(level);
            }
          });
        });
      });
    };

    collectLevels(this.originalControlTestMap);
    collectLevels(this.adjustedControlTestMap);

    return Array.from(levelSet).sort((a, b) => a - b);
  }

  private getControlTestEntries(materialItem: AssayData | undefined): QCControl[] {
    const key = materialItem?.reportContentItems?.reportContentItemId;
    if (!key) {
      return [];
    }

    const preferredMap = materialItem?.valueAssigned === 'AdjustedCalculate'
      ? this.adjustedControlTestMap
      : this.originalControlTestMap;

    let entries = preferredMap.get(key);

    if (!entries || !entries.length) {
      entries = this.originalControlTestMap.get(key) ?? this.adjustedControlTestMap.get(key);
    }

    return entries ?? [];
  }

  private computeControlTestLotOrder(): string[] {
    const seen = new Set<string>();
    const order: string[] = [];
    const seeds = [
      ...this.calResponseFilteredData,
      ...this.calResponseFilteredAdjustedData
    ];

    seeds.forEach(item => {
      const entries = this.getControlTestEntries(item);
      entries.forEach(entry => {
        const lot = entry?.lotNumber;
        if (lot && !seen.has(lot)) {
          seen.add(lot);
          order.push(lot);
        }
      });
    });

    return order;
  }

  private getControlTestControl(materialItem: AssayData | undefined, lot?: string): QCControl | undefined {
    const entries = this.getControlTestEntries(materialItem);
    if (!entries.length) {
      return undefined;
    }

    if (lot) {
      return entries.find(entry => entry?.lotNumber === lot);
    }

    const preferredLot = this.getPreferredTestLot(materialItem);
    if (preferredLot) {
      const match = entries.find(entry => entry?.lotNumber === preferredLot);
      if (match) {
        return match;
      }
    }

    return entries[0];
  }

  private getPreferredTestLot(materialItem: AssayData | undefined): string | undefined {
    const qcLot = materialItem?.reportContentItems?.qcLot;
    if (qcLot && qcLot.trim().length > 0) {
      return qcLot;
    }
    return materialItem?.reportContentItems?.lot;
  }

  private getControlTestVial(materialItem: AssayData, lot: string, levelNo: number): ControlTestVial | undefined {
    const vial = this.getControlTestControl(materialItem, lot)?.vials?.find(vial => {
      const level = typeof vial?.levelNo === 'number' ? vial.levelNo : Number(vial?.levelNo);
      return level === levelNo;
    });

    return vial as ControlTestVial | undefined;
  }

  private getControlTestCriteriaValue(materialItem: AssayData, lot: string, levelNo: number, variant: 'alert' | 'pass'): string {
    const vial = this.getControlTestVial(materialItem, lot, levelNo);
    if (!vial) {
      return '-';
    }

    const spec = variant === 'alert' ? vial.sdSpecAlert : vial.sdSpecPass;
    const num = variant === 'alert' ? vial.numSDsAlert : vial.numSDsPass;

    if (spec === undefined || spec === null || num === undefined || num === null) {
      return '-';
    }

    const magnitude = Number(spec) * Number(num);
    if (!isFinite(magnitude)) {
      return '-';
    }

    if (vial.sdSpecType === '%') {
      return `${formatComputedNumber(magnitude * 100)}%`;
    }

    return formatComputedNumber(magnitude);
  }

  private getControlTestRangeValue(materialItem: AssayData, lot: string, levelNo: number, variant: 'alert' | 'pass'): string {
    const vial = this.getControlTestVial(materialItem, lot, levelNo);
    const lower = this.displayOrDash(variant === 'alert' ? vial?.lowerLimitAlert : vial?.lowerLimitPass);
    const upper = this.displayOrDash(variant === 'alert' ? vial?.upperLimitAlert : vial?.upperLimitPass);

    if (lower === '-' && upper === '-') {
      return '-';
    }

    return `${lower} to ${upper}`;
  }

  private getControlTestStatusValue(materialItem: AssayData, lot: string, levelNo: number, variant: 'alert' | 'pass'): string {
    const vial = this.getControlTestVial(materialItem, lot, levelNo);
    const status = variant === 'alert' ? vial?.alertStatus : vial?.status;
    return this.formatStatusText(status);
  }

  private computeCalibrationCurveSectionStatus(): string {
    const statuses: string[] = [];
    this.calResponseCombinedData.forEach(item => {
      const monotonic = item?.calibrationCurve?.monotonic ?? item?.runStatus?.calCurveInformation?.monotonic;
      this.collectStatusValue(statuses, monotonic);
    });
    return this.evaluateSectionStatuses(statuses);
  }

  private computeCalCurveParameterSectionStatus(): string {
    const statuses: string[] = [];
    this.calResponseCombinedData.forEach(item => {
      const info = item?.runStatus?.calCurveInformation;
      if (!info) {
        return;
      }
      this.collectStatusValue(statuses, info.zeroConcentrationRfiResult);
      this.collectStatusValue(statuses, info.cutOffRfiResult);
      this.collectStatusValue(statuses, info.estimatedHighAssayRangeRfiResult);
      this.collectStatusValue(statuses, info.percentModulationResult);
      this.collectStatusValue(statuses, info.calBeadToBeadCVResult);
      this.collectStatusValue(statuses, info.lowEndSeparationResult);
      this.collectStatusValue(statuses, info.highEndSeparationResult);
    });
    return this.evaluateSectionStatuses(statuses);
  }

  private computeControlQcSectionStatus(): string {
    const statuses: string[] = [];
    this.calResponseCombinedData.forEach(item => {
      this.controlQcLevels.forEach(level => {
        this.collectStatusValue(statuses, this.getControlStatus(item, level));
      });
    });
    return this.evaluateSectionStatuses(statuses);
  }

  private computeControlTestSectionStatus(): string {
    const statuses: string[] = [];
    this.calResponseCombinedData.forEach(item => {
      this.controlTestLots.forEach(lot => {
        this.controlTestLevels.forEach(level => {
          this.collectStatusValue(statuses, this.getControlTestStatusAlert(item, lot, level));
          this.collectStatusValue(statuses, this.getControlTestStatusPass(item, lot, level));
        });
      });
    });
    return this.evaluateSectionStatuses(statuses);
  }

  private collectStatusValue(target: string[], status: string | undefined | null): void {
    const normalized = normalizeStatusValue(status);
    if (normalized) {
      target.push(normalized);
    }
  }

  private evaluateSectionStatuses(statuses: string[]): string {
    if (!statuses.length) {
      return 'NA';
    }
    return this.resultPassFailAlert(statuses);
  }
}
